import AuthService from "utilities/services/auth/auth-service";
import GroupService from "utilities/services/groups/group-service";
import IdentityRecord from "models/view-models/identity-record";
import NumberUtils from "utilities/number-utils";
import RoleService from "utilities/services/roles/role-service";
import RoleType from "utilities/enumerations/role-type";
import UserLoginRecord from "models/view-models/user-login-record";
import UserRoleGroupService from "utilities/services/user-role-groups/user-role-group-service";
import UserRoleOrganizationService from "utilities/services/users/user-roles/user-role-organization-service";
import UserRoleService from "utilities/services/users/user-roles/user-role-service";
import UserService from "utilities/services/users/user-service";
import UserSettingService from "utilities/services/user-settings/user-setting-service";
import { useCallback } from "react";
import ServiceResponseFactory from "utilities/services/service-response-factory";
import { ServiceResponse } from "@rsm-hcd/javascript-core";

/**
 * Custom hook providing utility functions for the current identity of the user
 */
export default function useIdentity() {
    const { get: getUser } = UserService.useGet();
    const { get: getAuth } = AuthService.useGet();
    const { list: listUserRoles } = UserRoleService.useList();
    const { list: listRoles } = RoleService.useList();
    const { list: listUserRoleGroups } = UserRoleGroupService.useList();
    const { list: listGroups } = GroupService.useList();
    const { list: listUserSettings } = UserSettingService.useList();
    const { list: listUserRoleOrganization } =
        UserRoleOrganizationService.useList();

    /**
     * Build the identity record including the current User, UserLogin and related navigation properties
     */
    const buildCurrentIdentity = async (
        userLogin: UserLoginRecord | undefined
    ): Promise<IdentityRecord | undefined> => {
        if (
            userLogin == null ||
            NumberUtils.isDefault(userLogin.id) ||
            NumberUtils.isDefault(userLogin.userId)
        ) {
            return;
        }

        const userLoginId = userLogin.id!;
        const userId = userLogin.userId!;

        const getUserPromise = getUser({
            id: userId,
        });
        const listGroupsPromise = listGroups({ userId });
        const listRolesPromise = listRoles();
        const getUserLoginPromise = getAuth({
            id: userLoginId,
        });
        const listUserRoleGroupsPromise = listUserRoleGroups({
            userId,
        });
        const listUserRolesPromise = listUserRoles({ userId });
        const listUserSettingsPromise = listUserSettings({
            userId,
        });

        const [
            getUserResponse,
            listGroupsResponse,
            listRolesResponse,
            getUserLoginResponse,
            listUserRoleGroupsResponse,
            listUserRolesResponse,
            listUserSettingsResponse,
        ] = await Promise.allSettled([
            getUserPromise,
            listGroupsPromise,
            listRolesPromise,
            getUserLoginPromise,
            listUserRoleGroupsPromise,
            listUserRolesPromise,
            listUserSettingsPromise,
        ]);

        const { resultObject: user, result: userResult } =
            settled(getUserResponse);

        if (userResult?.hasErrors()) {
            return undefined;
        }

        const { resultObjects: groups } = settled(listGroupsResponse);
        const { resultObjects: roles } = settled(listRolesResponse);
        const { resultObject: updatedUserLogin } =
            settled(getUserLoginResponse);
        const { resultObjects: userRoleGroups } = settled(
            listUserRoleGroupsResponse
        );
        const { resultObjects: userRoles } = settled(listUserRolesResponse);
        const { resultObjects: userSettings } = settled(
            listUserSettingsResponse
        );

        const organizationMemberRole = roles.find(
            (role) => role.roleType === RoleType.ORGANIZATION_MEMBER
        );
        const organizationUserRole = userRoles.find(
            (userRole) => userRole.roleId === organizationMemberRole?.id
        );

        const { resultObjects: userRoleOrganizations } =
            organizationUserRole && organizationUserRole.id !== undefined
                ? await listUserRoleOrganization({
                      userRoleId: organizationUserRole.id,
                  })
                : { resultObjects: undefined };

        return new IdentityRecord({
            groups,
            roles,
            user,
            userLogin: updatedUserLogin,
            userRoleOrganizations,
            userRoleGroups,
            userRoles,
            userSettings,
        });
    };

    return {
        buildCurrentIdentity: useCallback(buildCurrentIdentity, [
            getUser,
            listGroups,
            listRoles,
            getAuth,
            listUserRoleGroups,
            listUserRoles,
            listUserSettings,
            listUserRoleOrganization,
        ]),
    };
}

function settled<T>(
    promiseSettledResult: PromiseSettledResult<ServiceResponse<T>>
): ServiceResponse<T> {
    if (promiseSettledResult.status === "fulfilled") {
        return promiseSettledResult.value;
    }

    return ServiceResponseFactory.failedGet<T>({
        errorMessage: promiseSettledResult.reason,
    });
}
